import { Environment } from '@sb/library/base';
import { Throw } from '@sb/library/data-models';
import jwt_decode from 'jwt-decode';
import { CookieService } from 'ngx-cookie-service';
import { Subscription, timer } from 'rxjs';
import { ApiRoutes } from '../routes/api-routes';

export interface Authentication {
    ReturnUrl?: string;
    ProfileID: string;
    SchoolID: string;
    SchoolName: string;
    Name: string;
    Impersonating: boolean;
    ProfileMode: string;
    FirstName: string;
    LastName: string;
    EmailAddress: string;
    ElevioUserHash: string;
    IntercomCompanyID: string;
    IntercomUserID: string;
    iat: number;
    exp?: number;
    AuthSequence?: string;  // Unused
}

class Auth {
    private static _CookieService: CookieService;
    private static _Environment: Environment;

    private static _jwt?: string;
    private static _claims?: Authentication;
    private static _jwtRefreshSubscription?: Subscription;

    static SetFromCookieIfAvailable() {
        if (Auth._Environment.Authentication.PrimaryToken) {
            const cookieJWT = Auth._CookieService.get(Auth._Environment.Authentication.PrimaryToken);
            Auth.SetFromJWT(cookieJWT);
        }
    }

    static DeleteCookie(): void {
        Auth._CookieService.delete(Auth._Environment.Authentication.PrimaryToken!, '/');
    }

    static Clear() {
        Auth.SetFromJWT();
    }

    static SetFromJWT(jwt?: string) {
        Auth._jwtRefreshSubscription?.unsubscribe();
        Auth._claims = Auth.DecodeJWT(jwt);
        Auth._jwt = jwt;

        if (Auth._claims?.exp) {
            Auth.RefreshAuthentication();
        }
    }

    static DecodeJWT(jwt?: string) {
        return jwt ? jwt_decode<Authentication>(jwt) : undefined;
    }

    static NavigateToLogin(includePreviousLocation = true, loginURL = Auth._Environment.Application.LoginURL) {
        loginURL = Auth._claims?.ReturnUrl ?? loginURL;

        let previousLocation = '';
        if (includePreviousLocation) {
            const location = window.location.pathname + window.location.search + window.location.hash;
            previousLocation = `?PreviousPage=${location}`;
        }
        window.location.replace(`${loginURL}${previousLocation}`);
    }

    static UpdateCookie(jwt: string) {
        const cookieOptions = {
            path: '/',
            expires: Auth._claims?.exp ? new Date(Auth._claims.exp * 1000) : undefined
        }

        Auth._CookieService.set(Auth._Environment.Authentication.PrimaryToken!, jwt, cookieOptions);
    }

    private static RefreshAuthentication() {
        const currentTime = Math.round(new Date().getTime() / 1000) + 30;  // Allow 30 second grace time if we're very close to expiry
        const interval = (Auth._claims!.exp! - Auth._claims!.iat) >= 24*60*60 ? 60 : 5;  // an hour for long expiry (over a day); 5 minutes otherwise
        const delay = Math.max(0, (interval * 60) - (currentTime - Auth._claims!.iat)); // Adjust based on how far we are from issue time

        Auth._jwtRefreshSubscription = timer(delay * 1000).subscribe(Auth.RefreshJWT);
    }

    private static RefreshJWT(): void {
        ApiRoutes.Authentication.RefreshAuthentication.Call().subscribe({
            next: data => {
                Auth.SetFromJWT(data);
                if (Auth._Environment.Authentication.PrimaryToken) {
                    Auth.UpdateCookie(data);
                }
            },
            error: (error: typeof ApiRoutes.Authentication.RefreshAuthentication.Error) => {
                if (error.Status !== 401) {
                    // Don't care if this fails, just try again in a minute
                    Auth._jwtRefreshSubscription = timer(60000).subscribe(Auth.RefreshJWT);
                }
            }
        });
    }

    // Anything not defined on this class falls through to the decoded JWT claims, which must be set first.
    /**@internal*/ static CreateProxy() {
        return new Proxy(Auth as typeof Auth & Readonly<Authentication>, {
            get: (_, property: string) =>
                property in Auth ? Auth[property as keyof Auth]
              : Auth._claims ? Auth._claims[property as keyof Authentication]
              : Throw(`Cannot read '${property}': no authentication set.`)
        });
    }

    static get isAuthenticated() {
        return !!Auth._jwt;
    }

    static get JWT(): string {
        return Auth._jwt!;
    }
}

// Authentication is exposed purely as the proxy, which adds the interface to the class
export const Authentication = Auth.CreateProxy();
